import { useEffect } from "react";
import { siteConfig } from "@/lib/site-config";

/**
 * LeadConnector chat bubble.
 *
 * Previously a plain `<script defer>` in index.html. Even deferred it ran
 * during the initial render pass and its bundle was the single largest
 * contributor to Total Blocking Time — around 360 ms of blocked main thread
 * before a visitor could interact with anything.
 *
 * Nothing about the bubble is needed for first paint, so loading is held until
 * the browser is idle or the visitor first interacts, whichever comes first.
 * The bubble still appears on every page and behaves exactly as before; it just
 * no longer competes with the page's own content for the main thread.
 */
const IDLE_DELAY_MS = 2500;
const SCRIPT_ID = "lc-chat-widget-loader";
const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll"] as const;

const ChatWidget = () => {
  const { chatWidgetId, chatLoaderSrc, chatResourcesUrl } = siteConfig.widgets;

  useEffect(() => {
    if (!chatWidgetId) return;
    if (document.getElementById(SCRIPT_ID)) return;

    let done = false;
    const load = () => {
      if (done) return;
      done = true;
      cleanup();

      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = chatLoaderSrc;
      script.async = true;
      script.dataset.resourcesUrl = chatResourcesUrl;
      script.dataset.widgetId = chatWidgetId;
      document.body.appendChild(script);
    };

    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(load, { timeout: IDLE_DELAY_MS })
      : window.setTimeout(load, IDLE_DELAY_MS);

    const cleanup = () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle as number);
      else window.clearTimeout(idle as number);
      INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, load));
    };

    INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, load, { passive: true, once: true }));

    return cleanup;
  }, [chatWidgetId, chatLoaderSrc, chatResourcesUrl]);

  return null;
};

export default ChatWidget;
